import React from 'react';
import Icon from '@mdi/react';
import { mdiMicrophone } from '@mdi/js';
import Button from '../Button';
import { debounce } from 'throttle-debounce';
import swal from 'sweetalert';

class TextAreaFieldWithSpeech extends React.Component {
  state = {
    isSpeech: false,
    prevTranscript: '',
    deepgramWs: null,
    mediaStream: null,
  };
  textareaRef = React.createRef();

  componentWillUnmount() {
    this.stopSpeech();
  }

  speechCompare = (transcript) => {
    const { prevTranscript } = this.state;
    if (transcript && transcript !== prevTranscript) {
      this.speechInsert(transcript);
      this.setState({ prevTranscript: transcript });
    }
  };

  speechInsert = debounce(800, (transcript) => {
    const {
      input: { value, onChange },
    } = this.props;

    const endsWithPunct = /[.?!]\s?$/.test(value.trim());
    const prefix = value.trim() === '' ? '' : endsWithPunct ? ' ' : '. ';
    const newValue = `${value}${prefix}${transcript}`
      .replace(/new\s+line/gi, '\n')
      .replace(/new\s+paragraph/gi, '\n\n');

    onChange(newValue);
    if (this.textareaRef.current) {
      this.textareaRef.current.scrollTop =
        this.textareaRef.current.scrollHeight;
    }
  });

  startSpeech = async () => {
    const { speech, speechOn } = this.props;

    if (speech) {
      swal('Please turn off other recording');
      return;
    }

    const API_KEY = process.env.REACT_APP_DEEPGRAM_API_KEY;
    if (!API_KEY) {
      swal('Something went wrong. Please try again later.');
      return;
    }

    const mimeTypes = ['audio/webm', 'audio/mp4', 'audio/aac', 'audio/mpeg'];

    const maxRetries = 3;
    let retryCount = 0;
    let audioBuffer = [];

    const connectWebSocket = async () => {
      try {
        const deepgramWs = new WebSocket(
          'wss://api.deepgram.com/v1/listen?model=nova-2-medical&language=en&smart_format=true&numerals=true&measurements=true',
          ['token', API_KEY]
        );

        const mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        let selectedMimeType = null;
        for (const mimeType of mimeTypes) {
          if (MediaRecorder.isTypeSupported(mimeType)) {
            selectedMimeType = mimeType;
            break;
          }
        }

        if (!selectedMimeType) {
          throw new Error('No supported MIME type found for MediaRecorder');
        }

        const mediaRecorder = new MediaRecorder(mediaStream, {
          mimeType: selectedMimeType,
        });

        mediaRecorder.start(100);

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            if (deepgramWs.readyState === WebSocket.OPEN) {
              deepgramWs.send(event.data);
            } else {
              audioBuffer.push(event.data);
            }
          }
        };

        deepgramWs.onopen = () => {
          retryCount = 0;
          while (
            audioBuffer.length > 0 &&
            deepgramWs.readyState === WebSocket.OPEN
          ) {
            const audioData = audioBuffer.shift();
            deepgramWs.send(audioData);
          }
        };

        deepgramWs.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.channel && data.channel.alternatives[0].transcript) {
            const transcript = data.channel.alternatives[0].transcript;
            this.speechCompare(transcript);
          }
        };

        deepgramWs.onerror = (error) => {
          this.stopSpeech();
          swal('We encountered a connection issue. Please try again.');
        };

        deepgramWs.onclose = (event) => {
          if (retryCount < maxRetries && this.state.isSpeech) {
            retryCount += 1;
            setTimeout(connectWebSocket, 500 * retryCount);
          } else {
            this.stopSpeech();
            if (retryCount >= maxRetries) {
              swal(
                `Failed to maintain WebSocket connection: ${event.reason || 'Unknown reason'}`
              );
            }
          }
        };

        this.setState({ isSpeech: true, deepgramWs, mediaStream });
        speechOn();
      } catch (error) {
        this.stopSpeech();
        swal(`Unable to start speech recognition`);
      }
    };

    await connectWebSocket();
  };

  stopSpeech = () => {
    const { speechOff } = this.props;
    const { deepgramWs, mediaStream } = this.state;

    if (deepgramWs) {
      deepgramWs.onclose = null;
      deepgramWs.close();
    }
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
    }

    this.setState({
      isSpeech: false,
      prevTranscript: '',
      deepgramWs: null,
      mediaStream: null,
    });
    speechOff();
  };

  toggleMic = () => {
    if (this.state.isSpeech) {
      this.stopSpeech();
    } else {
      this.startSpeech();
    }
  };

  render() {
    const {
      input: { value, onChange },
      placeholder,
      meta: { touched, error },
    } = this.props;

    return (
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button noPadding onClick={this.toggleMic}>
            <Icon
              path={mdiMicrophone}
              size={1}
              color={this.state.isSpeech ? '#d44' : '#444'}
            />
          </Button>
          {this.state.prevTranscript && this.state.isSpeech && (
            <span style={{ color: '#d44', marginLeft: '0.5rem' }}>
              {this.state.prevTranscript} ...
            </span>
          )}
        </div>
        <textarea
          ref={this.textareaRef}
          style={{
            width: '100%',
            minHeight: 150,
            resize: 'vertical',
            padding: '0.75rem',
            marginTop: '0.5rem',
            fontSize: '1rem',
            borderRadius: '8px',
            border: '1px solid #ccc',
          }}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        {touched && error && (
          <div
            style={{ color: 'red', fontSize: '0.85rem', marginTop: '0.5rem' }}
          >
            {error}
          </div>
        )}
      </div>
    );
  }
}

export default TextAreaFieldWithSpeech;
